import { useEffect, useState } from 'react';
import { Loader2, AlertCircle, RefreshCw, Banknote } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useAuth } from '../contexts/AuthContext';
import { useCashCockpit, ALL_COMPANIES } from '../hooks/useCashCockpit';
import HatchPattern from '../components/cash-cockpit/charts/HatchPattern';
import HeroKpis from '../components/cash-cockpit/HeroKpis';
import OverviewTab from '../components/cash-cockpit/OverviewTab';
import ByEntityTab from '../components/cash-cockpit/ByEntityTab';
import ForecastTab from '../components/cash-cockpit/ForecastTab';
import FxExposureTab from '../components/cash-cockpit/FxExposureTab';
import CounterpartyTab from '../components/cash-cockpit/CounterpartyTab';
import CreditLinesTab from '../components/cash-cockpit/CreditLinesTab';
import IntercompanyTab from '../components/cash-cockpit/IntercompanyTab';
import PlannedEventsReadOnlyTab from '../components/cash-cockpit/PlannedEventsReadOnlyTab';
import DataQualityTab from '../components/cash-cockpit/DataQualityTab';
import SettingsTab from '../components/cash-cockpit/SettingsTab';
import InfoIcon from '../components/cash-cockpit/InfoIcon';

type TabId =
    | 'overview' | 'byEntity' | 'forecast' | 'fxExposure'
    | 'counterparty' | 'creditLines' | 'intercompany'
    | 'plannedEvents' | 'dataQuality' | 'settings';

const TAB_STORAGE_KEY = 'cashCockpit.activeTab';
const VALID_TABS: TabId[] = [
    'overview', 'byEntity', 'forecast', 'fxExposure',
    'counterparty', 'creditLines', 'intercompany',
    'plannedEvents', 'dataQuality', 'settings',
];

function loadActiveTab(): TabId {
    if (typeof window === 'undefined') return 'overview';
    try {
        const raw = window.sessionStorage.getItem(TAB_STORAGE_KEY);
        if (raw && (VALID_TABS as string[]).includes(raw)) return raw as TabId;
    } catch {
        // ignore
    }
    return 'overview';
}

const TABS: { id: TabId; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'byEntity', label: 'By Entity' },
    { id: 'forecast', label: 'Forecast (13W)' },
    { id: 'fxExposure', label: 'FX Exposure' },
    { id: 'counterparty', label: 'Counterparty Risk' },
    { id: 'creditLines', label: 'Credit Lines' },
    { id: 'intercompany', label: 'Intercompany' },
    { id: 'plannedEvents', label: 'Planned Events' },
    { id: 'dataQuality', label: 'Data Quality' },
    { id: 'settings', label: 'Settings' },
];

const TAB_INFO: Record<TabId, { title: string; body: JSX.Element }> = {
    overview: {
        title: 'Overview',
        body: (
            <>
                <p>Consolidated group cash in EUR over the last 24 months. The current month is outlined in amber.</p>
                <p>Click a month to filter the currency split below, or click a currency to filter the trend chart above.</p>
            </>
        ),
    },
    byEntity: {
        title: 'By Entity',
        body: (
            <>
                <p>Cash balance per legal entity over the selected time window, with staleness indicators.</p>
                <p>The entity filter is disabled here — this view always shows all companies for comparison.</p>
            </>
        ),
    },
    forecast: {
        title: 'Forecast (13W)',
        body: (
            <>
                <p><strong>13-week rolling cash forecast.</strong> Opening balance + AR inflows − AP outflows + planned events.</p>
                <p><strong>Automatic companies</strong> (bin, dbi, dch, dit): full AR/AP detail from BC.</p>
                <p><strong>Manual companies</strong> (dsp, dtr, dfr, dpt): opening + planned events only (badged &ldquo;Limited Forecast&rdquo;).</p>
            </>
        ),
    },
    fxExposure: {
        title: 'FX Exposure & Shock Scenario',
        body: (
            <>
                <p>Net position per entity × currency, translated to EUR.</p>
                <p>The <strong>FX shock slider</strong> simulates a uniform rate shift (default ±5%) to show the EUR impact of a currency stress event. <em>Shock Δ EUR</em> = EUR gain if the foreign currency strengthens by X%, or loss if it weakens.</p>
                <p>Use this to size hedging needs or understand the EUR exposure of a given position.</p>
            </>
        ),
    },
    counterparty: {
        title: 'Counterparty Risk',
        body: (
            <>
                <p>Top-10 concentration of cash by bank counterparty.</p>
                <p><strong>Without configured Groups:</strong> banks are rolled up by <code>BankCompanyName</code> (e.g. all UBS accounts across entities aggregated). For precise rollups (e.g. combining multiple banks as &ldquo;UBS Group&rdquo;), configure Groups in the Settings tab.</p>
            </>
        ),
    },
    creditLines: {
        title: 'Credit Lines',
        body: (
            <>
                <p>Bullet chart per credit facility — limit (line) vs. utilisation (bar).</p>
                <p>Red = over limit, amber = near limit (&gt;80%), green = healthy.</p>
                <p>Maturity alerts: critical ≤ 30 days, warning ≤ 90 days.</p>
            </>
        ),
    },
    intercompany: {
        title: 'Intercompany',
        body: (
            <>
                <p>Intercompany loans between group entities.</p>
                <p>Sources: automatic pull from BC G/L accounts (configurable in Settings → Intercompany Mapping) + manual entries on this tab.</p>
                <p>Direction inferred from account balance or loan name pattern.</p>
            </>
        ),
    },
    plannedEvents: {
        title: 'Planned Events',
        body: (
            <>
                <p>Read-only list of planned cash events used by the Forecast tab.</p>
                <p>Events are entered by country managers on the Bank Balance page.</p>
            </>
        ),
    },
    dataQuality: {
        title: 'Data Quality',
        body: (
            <>
                <p>Ops-grade signals: staleness (days since last balance), FX fallback (missing rate → spot-hold), orphan accounts (in BC but not classified).</p>
                <p>Use this tab to spot data refresh gaps before they break downstream reports.</p>
            </>
        ),
    },
    settings: {
        title: 'Settings',
        body: (
            <>
                <p>Admin-only configuration: Counterparty Groups (bank rollups) and Intercompany G/L Mapping.</p>
            </>
        ),
    },
};

export default function CashCockpitPage() {
    const { canView, canEdit, isLoading: permLoading } = usePagePermissions('/cash-cockpit');
    const { user } = useAuth();
    const isAdmin = user?.isAdmin ?? false;

    const cockpit = useCashCockpit();
    const [activeTab, setActiveTab] = useState<TabId>(loadActiveTab);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            window.sessionStorage.setItem(TAB_STORAGE_KEY, activeTab);
        } catch {
            // ignore
        }
    }, [activeTab]);

    // Lazy-load the active tab's data (skips if already cached for current filters).
    useEffect(() => {
        void cockpit.ensureTabLoaded(activeTab);
    }, [activeTab, cockpit.ensureTabLoaded]);

    if (permLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    if (!canView) {
        return (
            <div className="p-6 max-w-7xl mx-auto">
                <div className="flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-lg p-4">
                    <AlertCircle className="w-5 h-5" /> No permission for Cash Cockpit.
                </div>
            </div>
        );
    }

    // Filter out Settings tab if user cannot edit
    const visibleTabs = TABS.filter(t => t.id !== 'settings' || canEdit || isAdmin);

    return (
        <div className="p-6 max-w-[1400px] mx-auto">
            <HatchPattern />

            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <Banknote className="w-7 h-7 text-blue-600" />
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Cash Cockpit</h1>
                        <div className="text-xs text-gray-500">Group-wide liquidity · as of {cockpit.asOfDate}</div>
                    </div>
                </div>
                <button
                    onClick={cockpit.reload}
                    disabled={cockpit.loading}
                    className="inline-flex items-center gap-1 text-sm px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50"
                >
                    <RefreshCw className={`w-4 h-4 ${cockpit.loading ? 'animate-spin' : ''}`} /> Refresh
                </button>
            </div>

            {/* Filter bar */}
            {(() => {
                const filterDisabled = activeTab === 'byEntity' || activeTab === 'dataQuality';
                return (
                    <div className={`bg-white border border-gray-200 rounded-xl p-3 mb-4 flex items-center gap-3 flex-wrap ${filterDisabled ? 'opacity-60' : ''}`}>
                        <div className="text-xs uppercase text-gray-500 tracking-wide">Entities:</div>
                        <button
                            onClick={() => cockpit.setEntities('ALL')}
                            disabled={filterDisabled}
                            className={`text-xs px-2 py-1 rounded border ${cockpit.entities === 'ALL' ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} disabled:cursor-not-allowed`}
                        >ALL</button>
                        {ALL_COMPANIES.map(c => {
                            const active = cockpit.entities !== 'ALL' && cockpit.entities.includes(c);
                            return (
                                <button
                                    key={c}
                                    disabled={filterDisabled}
                                    onClick={() => {
                                        if (cockpit.entities === 'ALL') cockpit.setEntities([c]);
                                        else if (active) {
                                            const next = cockpit.entities.filter(x => x !== c);
                                            cockpit.setEntities(next.length ? next : 'ALL');
                                        } else cockpit.setEntities([...cockpit.entities, c]);
                                    }}
                                    className={`text-xs px-2 py-1 rounded border uppercase font-semibold ${active ? 'bg-gray-800 text-white border-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} disabled:cursor-not-allowed`}
                                >{c}</button>
                            );
                        })}
                        {filterDisabled && (
                            <span className="text-[11px] text-gray-500 italic ml-1">
                                Filter inactive on this view — shows all companies
                            </span>
                        )}
                        <div className="ml-auto flex items-center gap-2">
                            <label className="text-xs uppercase text-gray-500 tracking-wide">As of:</label>
                            <input
                                type="date" value={cockpit.asOfDate}
                                onChange={e => cockpit.setAsOfDate(e.target.value)}
                                className="border border-gray-300 rounded px-2 py-1 text-sm"
                            />
                        </div>
                    </div>
                );
            })()}

            {/* Error banner (partial-load warnings) */}
            {cockpit.error && (
                <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-4">
                    <AlertCircle className="w-4 h-4" /> {cockpit.error}
                </div>
            )}

            {/* Hero KPIs always on top */}
            <div className="mb-4">
                <HeroKpis overview={cockpit.overview} forecast={cockpit.forecast} />
            </div>

            {/* Tab strip */}
            <div className="flex border-b border-gray-200 mb-4 overflow-x-auto">
                {visibleTabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                            activeTab === tab.id
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab info banner */}
            {TAB_INFO[activeTab] && (
                <div className="flex items-center gap-2 mb-3 text-sm text-gray-700">
                    <span className="font-semibold">{TAB_INFO[activeTab].title}</span>
                    <InfoIcon title={TAB_INFO[activeTab].title}>
                        {TAB_INFO[activeTab].body}
                    </InfoIcon>
                </div>
            )}

            {/* Tab content */}
            {cockpit.loading && !cockpit.overview ? (
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
            ) : (
                <>
                    {activeTab === 'overview' && cockpit.overview && <OverviewTab data={cockpit.overview} />}
                    {activeTab === 'byEntity' && cockpit.byEntity && <ByEntityTab data={cockpit.byEntity} />}
                    {activeTab === 'forecast' && cockpit.forecast && <ForecastTab data={cockpit.forecast} />}
                    {activeTab === 'fxExposure' && cockpit.fxExposure && (
                        <FxExposureTab
                            data={cockpit.fxExposure}
                            shockPct={cockpit.fxShock}
                            onShockChange={cockpit.setFxShock}
                        />
                    )}
                    {activeTab === 'counterparty' && cockpit.counterparty && <CounterpartyTab data={cockpit.counterparty} />}
                    {activeTab === 'creditLines' && (
                        <CreditLinesTab
                            data={cockpit.creditLines}
                            canEdit={canEdit || isAdmin}
                            onReload={cockpit.reload}
                        />
                    )}
                    {activeTab === 'intercompany' && (
                        <IntercompanyTab
                            data={cockpit.intercompany}
                            canEdit={canEdit || isAdmin}
                            onReload={cockpit.reload}
                        />
                    )}
                    {activeTab === 'plannedEvents' && <PlannedEventsReadOnlyTab data={cockpit.plannedEvents} />}
                    {activeTab === 'dataQuality' && cockpit.dataQuality && <DataQualityTab data={cockpit.dataQuality} />}
                    {activeTab === 'settings' && (canEdit || isAdmin) && (
                        <SettingsTab
                            groups={cockpit.counterpartyGroups}
                            bankAccounts={cockpit.bankAccounts}
                            canEdit={canEdit || isAdmin}
                            onChanged={cockpit.reloadGroups}
                        />
                    )}
                </>
            )}
        </div>
    );
}
